import { useParams, Link, Navigate } from 'react-router-dom';
import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd } from '@/components/PageMeta';
import { CourseJsonLd, TeamXlEventsJsonLd } from '@/components/SchemaExtras';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Clock, Users, TrendingUp, Calendar, Check, ArrowRight, ArrowLeft } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { programmeData } from '@/data/programmes';
import { RelatedArticles } from '@/components/RelatedArticles';

export default function ProgrammeDetailPage() {
  const { slug } = useParams();
  const programme = programmeData[slug];

  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const [contentRef, contentVisible] = useScrollReveal(0.1);
  const [sessionRef, sessionVisible] = useScrollReveal(0.1);
  const [ctaRef, ctaVisible] = useScrollReveal(0.1);

  if (!programme) return <Navigate to="/programmes" replace />;

  const otherProgrammes = Object.entries(programmeData)
    .filter(([key]) => key !== slug)
    .slice(0, 3);

  return (
    <div className="min-h-screen">
      <PageMeta
        title={programme.title}
        description={programme.metaDesc}
        path={`/programmes/${slug}`}
      />
      <BreadcrumbJsonLd items={[
        { name: 'Accueil', url: '/' },
        { name: 'Programmes', url: '/programmes' },
        { name: programme.title },
      ]} />
      <CourseJsonLd
        slug={slug}
        title={programme.title}
        description={programme.metaDesc || programme.intro}
        duration={programme.duration}
        capacity={programme.capacity}
        level={programme.level}
        heroImage={programme.heroImage}
      />
      {slug === 'team-xl' && <TeamXlEventsJsonLd />}
      <TopBar />
      <Navigation />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-dark overflow-hidden" data-testid="programme-hero">
        <div className="absolute inset-0">
          <img decoding="async" src={programme.heroImage} alt={programme.title} className="w-full h-full object-cover opacity-25" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/80 to-dark/50" />
        </div>
        <div className="container-custom relative z-10 py-16 md:py-24">
          <div className={`transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Link to="/programmes" className="inline-flex items-center gap-2 text-white/60 hover:text-primary text-sm mb-6 transition-colors">
              <ArrowLeft className="h-4 w-4" /> Tous les programmes
            </Link>
            <div className={`inline-block bg-gradient-to-r ${programme.color} text-white px-4 py-1.5 rounded-full text-xs font-bold mb-4`}>
              {programme.subtitle}
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.95] mb-6">
              {programme.title}
            </h1>
            <p className="text-white/70 text-lg max-w-2xl leading-relaxed mb-8">
              {programme.intro}
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <Clock className="h-4 w-4 text-primary" />
                <span className="text-white text-sm font-medium">{programme.duration}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <Users className="h-4 w-4 text-primary" />
                <span className="text-white text-sm font-medium">{programme.capacity}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-white text-sm font-medium">{programme.level}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="text-white text-sm font-medium">{programme.frequency}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section ref={contentRef} className="section-padding bg-white" data-testid="programme-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className={`lg:col-span-3 transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <h2 className="font-heading text-3xl text-dark mb-6">EN DÉTAIL</h2>
              <div className="space-y-4">
                {programme.description.map((p, i) => (
                  <p key={i} className="text-muted-foreground leading-relaxed">{p}</p>
                ))}
              </div>

              <div className="mt-10">
                <h3 className="font-heading text-xl text-dark mb-2">À QUI S'ADRESSE CE PROGRAMME ?</h3>
                <p className="text-muted-foreground leading-relaxed">{programme.forWho}</p>
              </div>
            </div>

            <div className={`lg:col-span-2 transition-all duration-700 delay-200 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <Card className="p-6 bg-background border-0 sticky top-28">
                <h3 className="font-heading text-xl text-dark mb-5">LES BÉNÉFICES</h3>
                <div className="space-y-3">
                  {programme.benefits.map((b, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-primary" />
                      </div>
                      <span className="text-sm text-foreground">{b}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <Link to="/contact">
                    <Button className="w-full bg-primary hover:bg-primary-dark text-white font-heading tracking-wide py-5 rounded-lg">
                      RÉSERVER UN COURS
                    </Button>
                  </Link>
                  <Link to="/planning" className="block mt-3">
                    <Button variant="outline" className="w-full font-heading tracking-wide py-5 rounded-lg">
                      VOIR LE PLANNING
                    </Button>
                  </Link>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Session Breakdown */}
      <section ref={sessionRef} className="section-padding bg-background" data-testid="programme-session">
        <div className="container-custom max-w-4xl">
          <div className={`text-center mb-12 transition-all duration-700 ${sessionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-primary font-semibold text-sm tracking-widest uppercase">Déroulement</span>
            <h2 className="font-heading text-3xl md:text-4xl text-dark mt-3">UNE SÉANCE TYPE</h2>
          </div>
          <div className="space-y-4">
            {programme.sessionBreakdown.map((step, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-5 bg-white p-6 rounded-xl transition-all duration-500 ${
                  sessionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${200 + idx * 100}ms` }}
              >
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="font-heading text-primary text-sm">{idx + 1}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-heading text-base text-dark">{step.phase}</h3>
                    {step.time !== '—' && step.time !== 'Libre' && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">{step.time}</span>
                    )}
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Programmes */}
      <section className="section-padding bg-white" data-testid="other-programmes">
        <div className="container-custom">
          <h2 className="font-heading text-3xl text-dark mb-8 text-center">DÉCOUVRIR AUSSI</h2>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {otherProgrammes.map(([key, prog]) => (
              <Link key={key} to={`/programmes/${key}`} className="group">
                <Card className="overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                  <div className="relative h-36 overflow-hidden">
                    <img decoding="async" src={prog.heroImage} alt={prog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-3 left-4">
                      <h3 className="font-heading text-lg text-white">{prog.title}</h3>
                      <p className="text-white/60 text-xs">{prog.subtitle}</p>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="bg-primary py-16" data-testid="programme-cta">
        <div className="container-custom text-center">
          <div className={`transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="font-heading text-4xl text-white mb-4">PRÊT À ESSAYER ?</h2>
            <p className="text-white/80 text-base mb-8 max-w-lg mx-auto">
              Contactez-nous pour réserver votre premier cours de {programme.title}.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white hover:bg-white/90 text-primary font-heading tracking-wide px-10 py-7 rounded-xl transition-all duration-200 hover:scale-[1.03]">
                NOUS CONTACTER
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related articles */}
      <RelatedArticles
        keywords={[programme.title, 'CrossFit', 'Saint-Chamond']}
        title={`À lire — ${programme.title}`}
        limit={3}
      />

      <Footer />
    </div>
  );
}
